import { WindowRefService } from '@app/pages/shared/services/windowref.service';
import {
  Component,
  PLATFORM_ID,
  OnInit,
  OnDestroy,
  Inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { SpinnerService } from '@core/services/spinner.service';
import { Analytics } from './core/services/analytics/analytics.service';
import { CookieService } from './core/services/cookie.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { EventType } from './core/enums/meta.enum';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom, Subject } from 'rxjs';
import { Settings } from './core/services/settings.service';
import { ToasterService } from './core/services/toaster.service';
import { AuthService } from './core/services/auth.service';
import { Path } from './core/enums';
import { AppConstant } from './core/constant/app-constant';
import { HttpErrorResponse } from '@angular/common/http';
import { LocalStorageService } from './core/services/local-storage.service';
import { MultiLanguageService } from './core/services/multi-language.service';
import { AppService } from './app.service';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
declare global {
  interface Window {}
}
import { TvNavigationService } from '@app/core/services/tv-navigation.service';
import { ValentineThemeService } from './core/services/valentine-theme.service';
import { PaymentGatewayService } from './core/services/payment-gateway.service';
import { FacebookPixelService } from './core/services/analytics/facebook-pixel.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly title = 'ABC Talkies';
  private readonly PAGE = 'Application';
  message: any = null;

  isMobileWebAppDownloadViewEnabled: boolean = false;
  isValentineActive$ = this.valentineService.isActive$;

  private readonly destroy$ = new Subject<void>();

  constructor(
    public router: Router,
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly winRef: WindowRefService,
    public abcSpinner: SpinnerService,
    private readonly analytics: Analytics,
    private readonly cookieService: CookieService,
    private readonly deviceService: DeviceDetectorService,
    private readonly translate: TranslateService,
    private readonly settingService: Settings,
    private readonly toasterService: ToasterService,
    private readonly authService: AuthService,
    private readonly _localStorageService: LocalStorageService,
    private readonly _multiLanguageService: MultiLanguageService,
    private readonly _appService: AppService,
    private readonly tvNavigationService: TvNavigationService, // Inject to ensure eager loading
    private readonly valentineService: ValentineThemeService,
    private readonly paymentGatewayService: PaymentGatewayService,
    private readonly facebookPixel: FacebookPixelService,
  ) {}
  ngOnInit(): void {
    // Hide static loader
    const loader = document.getElementById('app-loader');
    if (loader) {
      setTimeout(() => {
        loader.classList.add('hidden');
        setTimeout(() => loader.remove(), 600);
      }, 1000); // Small delay to ensure smooth transition
    }

    const user = JSON.parse(
      this._localStorageService.get(AppConstant.Keys.user),
    );

    const savedLang = this._localStorageService.get('appLanguage');
    if (savedLang) {
      this._multiLanguageService.setLanguage(savedLang);
    } else if (user?.defaultLanguage) {
      this._multiLanguageService.setLanguage(user.defaultLanguage);
    } else {
      const browserLang = this.translate.getBrowserLang();
      if (browserLang) this._multiLanguageService.setLanguage(browserLang);
      else this._multiLanguageService.setLanguage('en');
    }

    if (isPlatformBrowser(this.platformId)) {
      this.checkVersion();
      this.addFirebase();

      // auto role change when role change in other tab
      window.addEventListener('storage', (event) => {
        if (event.key === '_user') {
          try {
            const updatedUser = event.newValue
              ? JSON.parse(event.newValue)
              : null;
            this.authService.userSubject.next(updatedUser);
            const currentPath = this.router.url;
            let targetPath: string | null = null;

            if (updatedUser?.role === 'FILM_MAKER') {
              targetPath = `/${Path.App}/${Path.Filmmaker}/${Path.Dashboard}`;
            } else if (updatedUser?.role === 'VIEWER') {
              targetPath = `/${Path.Home}`;
            }

            if (targetPath && currentPath !== targetPath) {
              this.router.navigate([targetPath]).catch((err) => {
                console.error('Navigation failed:', err);
              });
            }
          } catch (err) {
            console.error('Error parsing user from localStorage event:', err);
            this.authService.logout(); // optional fallback
          }
        }

        if (event.key === '_token') {
          // Listen for token updates
          const updatedToken = event.newValue
            ? JSON.parse(event.newValue)
            : null;
          this.authService.tokenSubject.next(updatedToken); // Update token in the BehaviorSubject
        }
      });

      // Facebook Pixel — fire PageView on every SPA route change.
      // The initial PageView on hard load is already handled by index.html.
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      ).subscribe(() => {
        this.facebookPixel.trackPageView();
      });
    }

    // Whenever user is logged in, send device details
    this.authService.isLoggedIn
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        filter((loggedIn) => loggedIn === true),
      )
      .subscribe({
        next: () => {
          this._appService.sendDeviceDetails().subscribe({
            next: (res) => {
              console.log(res);
            },
            error: (error: HttpErrorResponse) => {
              console.error('Error sending device details:', error);
            },
          });
        },
      });

    this.analytics.customAnalytics('Session Start', {
      time: new Date().toISOString(),
      page: this.PAGE,
      eventType: EventType.success,
    });

    this.checkSettings();
  }

  checkSettings() {
    this.settingService.getFeatureSettings().subscribe({
      next: (res) => {
        this.isMobileWebAppDownloadViewEnabled =
          res.data.settings.isMobileWebAppDownloadViewEnabled;
        if (this.isMobileWebAppDownloadViewEnabled) this.redirectMobileUsers();

        // Set payment gateway from feature settings
        if (res.data.settings.paymentGateway) {
          this.paymentGatewayService.setGateway(
            res.data.settings.paymentGateway,
          );
        }
      },
      error: (error: HttpErrorResponse) => {
        if (error.status !== AppConstant.PORT.Forbidden)
          this.toasterService.error(error?.error?.message ?? error.message);
      },
    });
  }

  private redirectMobileUsers(): void {
    const currentURL = this.router.url;
    const cookie = this.cookieService.getCookieByName('kGlnOFjvPy');

    if (
      this.deviceService.isMobile() &&
      (currentURL === '' || currentURL === '/') &&
      cookie !== '1' &&
      !location.pathname.includes('qr-code') &&
      !location.pathname.includes('film-maker/contest-registration')
    ) {
      const os = this.deviceService.os.toLowerCase();
      let targetRoute: string | null = null;

      if (os === 'ios') {
        targetRoute = 'static/device/ios';
      } else if (os === 'android') {
        targetRoute = 'static/device/android';
      } else {
        targetRoute = 'static/device/all';
      }

      if (targetRoute) {
        this.router.navigate([targetRoute]);
      }
    }
  }

  async checkVersion(): Promise<void> {
    const versionUrl = `assets/version.json?v=${new Date().getTime()}`;
    const existingVersion = localStorage.getItem('appVersion');

    try {
      const res = await fetch(versionUrl);
      if (!res.ok) throw new Error('Failed to fetch version.json');

      const data = await res.json();
      const newVersion = data.appVersion;

      // If no version saved (old users), force clear
      if (!existingVersion) {
        console.warn('Old user detected. Clearing cache...');
        await this.clearBrowserCache();
        localStorage.setItem('appVersion', newVersion);
        location.reload();
        return;
      }

      if (existingVersion !== newVersion) {
        localStorage.setItem('appVersion', newVersion);

        await lastValueFrom(this.translate.reloadLang('en'));

        const links = document.querySelectorAll(
          'link[rel="stylesheet"], script',
        );
        links.forEach((resource) => {
          const attr = resource.tagName === 'LINK' ? 'href' : 'src';
          const val = resource.getAttribute(attr);
          if (val) {
            resource.setAttribute(
              attr,
              `${val.split('?')[0]}?v=${new Date().getTime()}`,
            );
          }
        });

        location.reload();
      }
    } catch (err) {
      console.error('Version check error:', err);
    }
  }

  private async clearBrowserCache(): Promise<void> {
    try {
      // 1. Clear HTTP cache (e.g., for Safari, iOS Safari)
      if ('caches' in window) {
        const keys = await caches.keys();
        for (const key of keys) {
          await caches.delete(key);
        }
      }

      // 2. Clear service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const reg of registrations) {
          await reg.unregister();
        }
      }

      // 3. Clear localStorage just in case
      localStorage.removeItem('appVersion');
    } catch (err) {
      console.error('Error clearing caches:', err);
    }
  }

  private addFirebase() {
    // COMMENTED OUT FOR PWA SUPPORT: Manual registration of firebase-messaging-sw.js conflicts with ngsw-worker.js.
    // AngularFire or the main SW should handle this to preserve PWA offline capabilities.
    /*
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/firebase-messaging-sw.js').then(
        () => { },
        (error) => {
          console.log('Service worker registration failed:', error);
        }
      );
    } else {
      console.log('Service workers are not supported.');
    }
    */
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.analytics.customAnalytics('Session End', {
      time: new Date().toISOString(),
      page: this.PAGE,
      eventType: EventType.success,
    });
  }
}
